/**
 * Draft persistence for the Create Study wizard.
 *
 * A draft is a real Project with status='draft' and is_active=false (so it is
 * kept out of the public respondent flow). The full wizard state is stored in
 * the project's `draft_data` JSON so the researcher can close the wizard and
 * resume exactly where they left off. Binary File objects can't live in JSON,
 * so file-backed stimuli are uploaded to the draft project and referenced by
 * their persisted id/url instead.
 */
import type { Project, CreateProjectRequest, StudyDraftData } from '@/services/projectTypes'
import { DEFAULT_CALIBRATION_VARIANT } from '@/lib/calibrationVariants'
import { METRIC_KEYS } from './fixedMetric'
import type { StimulusDraft, StudyDraft } from './types'

/** A fresh 6-digit access code, used as a fallback when hydrating. */
const genAccessCode = () => String(Math.floor(100000 + Math.random() * 900000))

/** Same INITIAL_DRAFT the wizard opens with, kept here so hydration can fill
 *  any keys an older draft_data blob is missing. */
export const INITIAL_DRAFT: StudyDraft = {
  name: '',
  client: '',
  targetRespondents: '150',
  description: '',
  goals: [],
  audience: '',
  stimuli: [],
  eyeTracking: false,
  calibrationVariant: DEFAULT_CALIBRATION_VARIANT,
  questionTiming: 'end',
  questions: [],
  demographics: [],
  metricFields: [...METRIC_KEYS],
  questionnaireTemplateId: null,
  responseLimit: '150',
  expiry: '',
  isPublic: false,
  requireAccessCode: false,
  accessCode: '',
  inviteRespondentIds: [],
  inviteTagIds: [],
}

/** A stimulus stripped of its File so it can be stored as JSON. The binary is
 *  preserved separately by uploading it to the draft project (serverId/url). */
function serializeStimulus(s: StimulusDraft): Omit<StimulusDraft, 'file'> {
  const { file, ...rest } = s
  void file
  return rest
}

/** Build the JSON-safe wizard snapshot stored in `draft_data`. */
export function serializeDraft(draft: StudyDraft): StudyDraftData {
  return {
    ...draft,
    stimuli: draft.stimuli.map(serializeStimulus),
  } as unknown as StudyDraftData
}

/** The real Project columns a draft mirrors, so the study list shows a proper
 *  name / client / target without having to read draft_data. */
export function draftToProjectFields(draft: StudyDraft): Partial<CreateProjectRequest> {
  const target = Math.round(Number(draft.targetRespondents))
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    client: draft.client.trim(),
    ...(Number.isFinite(target) && target > 0 ? { target_respondents: target } : {}),
    question_timing: draft.questionTiming,
    require_access_code: draft.requireAccessCode,
    access_code: draft.requireAccessCode ? draft.accessCode.trim() : '',
    eye_tracking_enabled: draft.eyeTracking,
    calibration_variant: draft.calibrationVariant,
    is_public: draft.isPublic,
    ...(draft.questionnaireTemplateId != null
      ? { questionnaire_template: draft.questionnaireTemplateId }
      : {}),
  }
}

/** True when a draft has enough in it to be worth persisting (avoids creating
 *  empty draft projects the moment the wizard opens). */
export function draftHasContent(draft: StudyDraft): boolean {
  return (
    draft.name.trim().length > 0 ||
    draft.description.trim().length > 0 ||
    draft.client.trim().length > 0 ||
    draft.stimuli.length > 0 ||
    draft.questions.length > 0 ||
    draft.demographics.length > 0
  )
}

/** Reconstruct wizard state from a saved draft project. Prefers the rich
 *  `draft_data` blob; falls back to the project's own columns + stimuli for
 *  drafts saved before draft_data existed (or projects opened for editing). */
export function hydrateDraft(project: Project): StudyDraft {
  const base: StudyDraft = {
    ...INITIAL_DRAFT,
    accessCode: genAccessCode(),
  }

  const data = project.draft_data
  if (data && typeof data === 'object') {
    const d = data as Partial<StudyDraft>
    const merged: StudyDraft = {
      ...base,
      ...d,
      // Guard against a partial/older blob missing array fields.
      goals: d.goals ?? base.goals,
      stimuli: (d.stimuli as StimulusDraft[] | undefined) ?? base.stimuli,
      questions: d.questions ?? base.questions,
      demographics: d.demographics ?? base.demographics,
      // A draft saved before the metric could be switched off asks both
      // fields, which is what it was launched to do.
      metricFields: d.metricFields ?? base.metricFields,
      inviteRespondentIds: d.inviteRespondentIds ?? base.inviteRespondentIds,
      inviteTagIds: d.inviteTagIds ?? base.inviteTagIds,
      accessCode: d.accessCode?.trim() ? d.accessCode : base.accessCode,
    }
    return merged
  }

  // Fallback: rebuild from the project columns + already-uploaded stimuli.
  return {
    ...base,
    name: project.name ?? '',
    description: project.description ?? '',
    client: project.client ?? '',
    targetRespondents: String(project.target_respondents ?? base.targetRespondents),
    questionTiming: project.question_timing ?? base.questionTiming,
    requireAccessCode: !!project.require_access_code,
    eyeTracking: !!project.eye_tracking_enabled,
    calibrationVariant: project.calibration_variant ?? base.calibrationVariant,
    isPublic: !!project.is_public,
    accessCode: project.access_code?.trim() ? project.access_code : base.accessCode,
    questionnaireTemplateId: project.questionnaire_template ?? null,
    stimuli: (project.stimuli ?? []).map((s, i) => ({
      id: `stim-server-${s.id}`,
      type: s.type,
      name: s.title || s.url || `#${i + 1}`,
      label: `#${i + 1}`,
      meta: '',
      status: 'ready' as const,
      serverId: s.id,
      url: s.url || s.storage_url || (s.type === 'url' ? s.title : undefined),
      payload: s.payload || undefined,
      exposureMs: s.exposure_ms ?? undefined,
      ...(s.type === 'figma'
        ? {
            goalNodeId: s.config?.goal_node_id ?? '',
            figmaFrames: (s.config?.frames ?? []).map((f) => ({ node_id: f.node_id, name: f.name })),
          }
        : {}),
    })),
  }
}
